import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { CommunicationUserIdentifier } from "@azure/communication-common";
import {
  startConsultation,
  completeConsultation,
  rejoinConsultation,
  getPatientInfo,
  getConsultationById,
} from "../../Common/axios_instance/video_https/video.https";
import { getDoctorUserTokenFun, addUserToRoomFun } from "../../Pages/azure/action/action";
import { CustomCallView, CustomCallViewHandle } from "../../Pages/azure/views/CustomCallView";
import { callManager } from "../../Pages/azure/call-manager";
import { useToast } from "../../Common/hooks/useToast";
import { Consultation, PatientInfo } from "../../Common/interfaces/doctor.interfaces";
import style from "./consultation_active.module.css";

export const DoctorConsultationActive: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [consultation, setConsultation] = useState<Consultation | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfo | null>(null);
  const [loadingPatient, setLoadingPatient] = useState(true);
  const [showPatientPanel, setShowPatientPanel] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // ACS video call state
  const [callToken, setCallToken] = useState<string | null>(null);
  const [callUserId, setCallUserId] = useState<CommunicationUserIdentifier | null>(null);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [callReady, setCallReady] = useState(false);

  const hasStartedRef = useRef(false);
  const callViewRef = useRef<CustomCallViewHandle | null>(null);
  const [isMicOn, setIsMicOn] = useState(callManager.getState().isMicOn);
  const [isCameraOn, setIsCameraOn] = useState(callManager.getState().isCameraOn);
  const isMountedRef = useRef(true);
  const { toast, showToast } = useToast();

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Register the call-ended handler directly on callManager so it fires even
  // when the doctor is on the Notes/Rx screen (this component unmounted).
  // Using a ref to hold the latest consultationId avoids stale closure.
  const consultationIdRef = useRef<string | undefined>(id);
  useEffect(() => { consultationIdRef.current = id; }, [id]);

  useEffect(() => {
    const handleEnded = () => {
      const cid = consultationIdRef.current;
      if (cid) {
        completeConsultation(cid).catch(() => {});
      }
      // navigate is stable across renders in react-router v6
      navigate(`/consultation/${cid}/complete`, { state: { patientEnded: true } });
    };
    callManager.addOnCallEnded(handleEnded);
    // Clear the handler when the consultation screen fully unmounts
    return () => { callManager.removeOnCallEnded(handleEnded); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      // Store consultation ID so getUserToken can send it for ACS identity reuse
      sessionStorage.setItem("activeConsultationId", id);
      sessionStorage.removeItem(`patientEnded:${id}`);
      fetchConsultationDetails();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchConsultationDetails = async () => {
    try {
      setLoadingPatient(true);
      getPatientInfo(id!)
        .then((patientRes) => {
          if (patientRes.data?.data) setPatientInfo(patientRes.data.data);
        })
        .catch((e) => console.warn("Failed to load patient info:", e))
        .finally(() => setLoadingPatient(false));

      const consultRes = await getConsultationById(id!);
      const data = consultRes.data?.data;
      if (data) {
        setConsultation(data);
        // Timer is handled reactively by the CallTimer subcomponent below

        // If accepted but not yet started, auto-start and set up video
        if (data.status === "accepted" && !hasStartedRef.current) {
          hasStartedRef.current = true;
          try {
            const startRes = await startConsultation(id!);
            const roomData = startRes.data?.data;
            if (roomData?.roomId) {
              if (roomData.serviceName === "100ms") {
                // 100ms uses separate page
                navigate(`/100ms/preview/${roomData.roomId}/host`);
              } else {
                // Azure — set up inline video call
                await setupAzureCall(roomData.roomId);
                // Re-fetch details to getstarted_at
                await fetchConsultationDetails();
              }
            }
          } catch (err: any) {
            showToast(err.response?.data?.message || "Failed to start call room", "error");
          }
        }

        // If already ongoing, set up video with existing roomId
        if (data.status === "ongoing" && data.room_id && !callReady) {
          await setupAzureCall(data.room_id);
        }

        // If completed (rejoin within 1hr), call backend to flip status back to ongoing first
        if (data.status === "completed" && data.room_id && !callReady) {
          try {
            await rejoinConsultation(id!);
            // Re-fetch to get updated status
            const refreshRes = await getConsultationById(id!);
            if (refreshRes.data?.data) setConsultation(refreshRes.data.data);
            await setupAzureCall(data.room_id);
          } catch (rejoinErr: any) {
            showToast(rejoinErr.response?.data?.message || "Room expired — cannot rejoin", "error");
            navigate(`/consultation/${id}/detail`);
            return;
          }
        }
      }
    } catch (e) {
      showToast("Failed to fetch consultation details", "error");
    }
  };

  const hasSetupCallRef = useRef(false);

  const setupAzureCall = async (rid: string) => {
    if (hasSetupCallRef.current) return;
    hasSetupCallRef.current = true;    try {
      // Use doctor-specific endpoint — reuses same ACS identity per consultation (no ghost on rejoin)
      const tokenData = await getDoctorUserTokenFun(id!);
      if (!tokenData?.token || !tokenData?.user) {
        showToast("Failed to get call token", "error");
        return;
      }

      const acsUserId: string = tokenData.user.communicationUserId;

      // Add the doctor to the ACS room as Presenter.
      // The room is created by startConsultation before this runs, so it exists.
      const addResult = await addUserToRoomFun(acsUserId, rid, "Presenter" as any);
      console.log('[ACS] addUserToRoom result:', addResult, 'userId:', acsUserId, 'roomId:', rid);

      setCallToken(tokenData.token);
      setCallUserId({ communicationUserId: acsUserId });
      setRoomId(rid);
      setCallReady(true);
    } catch (err: any) {
      showToast(err?.message || "Failed to set up video call", "error");
    }
  };

  const handleEndCall = async () => {
    if (!id) return;
    if (!window.confirm("Are you sure you want to end this consultation? This will close the call room permanently.")) return;
    setIsProcessing(true);
    try {
      // Hang up the call via CallManager — releases camera/mic immediately
      await callManager.hangUp();
      // Then mark consultation complete on backend
      await completeConsultation(id);
      showToast("Consultation completed successfully!", "success");
      setTimeout(() => {
        navigate(`/consultation/${id}/complete`);
      }, 1500);
    } catch (error: any) {
      showToast(error.response?.data?.message || "Failed to end call", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const doctorName = localStorage.getItem("doctorName") || sessionStorage.getItem("doctorName") || "Doctor";

  return (
    <div className={style.doctorBody}>
      {toast.show && (
        <div className={`${style.toastMsg} ${style.show} ${style[toast.type]}`}>
          <span className="material-icons-round">
            {toast.type === "success" ? "check_circle" : "error"}
          </span>
          {toast.msg}
        </div>
      )}

      {/* Video Area — ACS CallScreen embedded inline */}
      <div className={style.videoContainer}>
        {callReady && callToken && callUserId && roomId ? (
          <div style={{ width: "100%", height: "100%" }}>
            <CustomCallView
              ref={callViewRef}
              token={callToken}
              userId={callUserId.communicationUserId}
              roomId={roomId}
              displayName={doctorName}
              waitingText="Waiting for patient..."
              onCallEnded={async () => {
                  // Handler is registered directly on callManager above.
                  // This prop is kept for the case where the doctor is still
                  // on this screen — callManager will fire the handler.
                }}
              onMicToggle={(v) => setIsMicOn(v)}
              onCameraToggle={(v) => setIsCameraOn(v)}
              onParticipantJoined={(name) => showToast(`${name} joined the call`, "success")}
            />
          </div>
        ) : (
          <div className={style.videoPlaceholder}>
            <span className="material-icons-round">person</span>
            <p>{consultation?.patient_name || "Patient Screen"}</p>
            <div style={{ marginTop: "12px" }}>
              {consultation?.status === "ongoing" ? (
                <span style={{ fontSize: "13px", color: "var(--success)" }}>Setting up video call...</span>
              ) : (
                <span style={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.4)" }}>Waiting to join call</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Self Video PIP — hidden when CallScreen is active (it has its own PIP) */}
      {!callReady && (
        <div className={style.selfVideo}>
          <span className="material-icons-round">person</span>
        </div>
      )}

      {/* Top Bar */}
      <div className={style.topBar}>
        <div className={style.topBarLeft}>
          <button className={style.backBtn} onClick={() => {
            if (callReady) {
              if (window.confirm("You are currently in an active call. Leaving this screen will disconnect the video call. Are you sure you want to go back?")) {
                callManager.hangUp();
                navigate("/dashboard");
              }
            } else {
              navigate("/dashboard");
            }
          }}>
            <span className="material-icons-round">arrow_back</span>
          </button>
          <div className={style.callInfo}>
            <span className={style.callInfoPatientName}>{consultation?.patient_name || "Loading..."}</span>
            <span className={style.callInfoTicketId}>{consultation?.ticket_id ? `Ticket ID: ${consultation.ticket_id}` : "N/A"}</span>
          </div>
        </div>
        <div className={style.callTimer}>
          <span className={style.callTimerDot} />
          {consultation?.started_at ? (
             <CallTimer
              startedAt={consultation.started_at}
              accumulatedSeconds={consultation.call_duration_seconds || 0}
            />
          ) : (
            "00:00"
          )}
        </div>
      </div>

      {/* Bottom Controls Bar */}
      <div className={style.controlsBar}>
        {/* Actions Row */}
        <div className={style.actionsRow}>
          <button className={style.actionPill} onClick={() => setShowPatientPanel(true)}>
            <span className="material-icons-round">person</span>
            Patient Info
          </button>
          <Link to={`/consultation/${id}/notes`} className={style.actionPill}>
            <span className="material-icons-round">edit_note</span>
            Notes
          </Link>
          <Link to={`/consultation/${id}/prescription`} className={style.actionPill}>
            <span className="material-icons-round">medication</span>
            Rx
          </Link>
        </div>

        {/* End Call button — visible even when ACS controls bar is shown */}
        <div className={style.controlsMain} style={{ marginTop: "16px", marginBottom: 0 }}>
          <button
            className={`${style.controlBtn} ${!isMicOn ? style.active : ""}`}
            onClick={() => callViewRef.current?.toggleMic()}
            disabled={!callReady}
          >
            <span className="material-icons-round">{isMicOn ? "mic" : "mic_off"}</span>
          </button>
          <button
            className={`${style.controlBtn} ${!isCameraOn ? style.active : ""}`}
            onClick={() => callViewRef.current?.toggleCamera()}
            disabled={!callReady}
          >
            <span className="material-icons-round">{isCameraOn ? "videocam" : "videocam_off"}</span>
          </button>
          {isCameraOn && (
            <button
              className={style.controlBtn}
              onClick={() => callViewRef.current?.switchCamera()}
              disabled={!callReady}
            >
              <span className="material-icons-round">flip_camera_ios</span>
            </button>
          )}
          <button
            className={`${style.controlBtn} ${style.endCall}`}
            onClick={handleEndCall}
            disabled={isProcessing || !callReady}
          >
            <span className="material-icons-round">call_end</span>
          </button>
        </div>
      </div>

      {/* Patient Info Drawer Overlay */}
      <div
        className={`${style.docOverlay} ${showPatientPanel ? style.visible : ""}`}
        onClick={() => setShowPatientPanel(false)}
      />

      {/* Patient Profile Slide-up Drawer */}
      <div className={`${style.patientPanel} ${showPatientPanel ? style.open : ""}`}>
        <div className={style.panelHandle} />
        <div className={style.panelHeader}>
          <h2>Patient Profile</h2>
          <button className={style.panelClose} onClick={() => setShowPatientPanel(false)}>
            <span className="material-icons-round">close</span>
          </button>
        </div>

        <div className={style.patientIdentity}>
          <div className={style.patientAvatarLg}>
            {consultation?.patient_name ? consultation.patient_name.slice(0, 2).toUpperCase() : "PT"}
          </div>
          <div className={style.patientIdentityInfo}>
            <div className={style.name}>{patientInfo?.patient_name || consultation?.patient_name || "Patient"}</div>
            <div className={style.meta}>
              {patientInfo?.patient_age || consultation?.patient_age || "N/A"} yrs •{" "}
              {patientInfo?.patient_gender || consultation?.patient_gender || "N/A"}
              {patientInfo?.blood_group ? ` • ${patientInfo.blood_group}` : ""}
            </div>
            <span className={style.ticket}>{consultation?.ticket_id || "N/A"}</span>
          </div>
        </div>

        <div className={style.panelSection}>
          <div className={style.panelSectionTitle}>Basic Information</div>
          <div className={style.infoRow}>
            <span className="material-icons-round">cake</span>
            <span className={style.rowLabel}>Date of Birth</span>
            <span className={style.rowValue}>
              {patientInfo?.date_of_birth
                ? new Date(patientInfo.date_of_birth).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                : "N/A"}
            </span>
          </div>
          <div className={style.infoRow}>
            <span className="material-icons-round">water_drop</span>
            <span className={style.rowLabel}>Blood Group</span>
            <span className={style.rowValue}>{patientInfo?.blood_group || "N/A"}</span>
          </div>
          <div className={style.infoRow}>
            <span className="material-icons-round">folder_open</span>
            <span className={style.rowLabel}>Medical Profile</span>
            <span className={style.rowValue}>
              {loadingPatient ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "var(--on-surface-variant)" }}>
                  <div className={style.spinner} />
                  Loading...
                </div>
              ) : patientInfo?.medical_profile_pdf_url ? (
                <button
                  onClick={() => window.open(patientInfo.medical_profile_pdf_url!, "_blank")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    background: "#e8f0fe",
                    color: "#1a73e8",
                    border: "none",
                    fontWeight: 600,
                    fontSize: "12px",
                    cursor: "pointer"
                  }}
                >
                  Open Document
                </button>
              ) : (
                "N/A"
              )}
            </span>
          </div>
        </div>

        {patientInfo?.chronic_conditions && patientInfo.chronic_conditions.length > 0 && (
          <div className={style.tagSection}>
            <div className={style.tagSectionTitle}>Known Conditions</div>
            <div className={style.medicalTags}>
              {patientInfo.chronic_conditions.map((c: string) => (
                <span key={c} className={style.medicalTag}>{c}</span>
              ))}
            </div>
          </div>
        )}

        {patientInfo?.allergies && patientInfo.allergies.length > 0 && (
          <div className={style.tagSection}>
            <div className={style.tagSectionTitle}>Allergies</div>
            <div className={style.medicalTags}>
              {patientInfo.allergies.map((a: string) => (
                <span key={a} className={`${style.medicalTag} ${style.allergy}`}>{a}</span>
              ))}
            </div>
          </div>
        )}



        <div className="panel-bottom-space" style={{ height: "40px" }} />
      </div>
    </div>
  );
};

export const CallTimer: React.FC<{ startedAt: string; accumulatedSeconds?: number }> = ({ startedAt, accumulatedSeconds = 0 }) => {
  const [time, setTime] = useState("00:00");
  useEffect(() => {
    const startTime = new Date(startedAt).getTime();
    const tick = () => {
      const sessionSecs = Math.max(0, Math.floor((Date.now() - startTime) / 1000));
      const totalSecs = accumulatedSeconds + sessionSecs;
      const mins = Math.floor(totalSecs / 60).toString().padStart(2, "0");
      const s = (totalSecs % 60).toString().padStart(2, "0");
      setTime(`${mins}:${s}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt, accumulatedSeconds]);

  return <>{time}</>;
};
